import { Drawer, Input, Table, Tag, Select, Button, Typography, Spin, Alert, Space } from 'antd';
import type { TableColumnsType } from 'antd';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { formatISODateTime } from 'utils/dateTimeUtils';
import { GET_CLIENT_CARTON_DETAIL, GetClientCartonDetailResponse } from '../graphql/queries/getClientCartonDetail';
import { ClientCartonParcel } from '../graphql/types/ClientCartonTypes';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import { getUserInfo } from 'utils/auth';

const { Text } = Typography;

interface ClientCartonDetailDrawerProps {
    visible: boolean;
    clientCartonNo: string | null;
    id: number | null;
    onClose: () => void;
}

const ClientCartonDetailDrawer: React.FC<ClientCartonDetailDrawerProps> = ({
    visible,
    clientCartonNo,
    id,
    onClose,
}) => {
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');

    const { data, loading, error, refetch } = useQuery<GetClientCartonDetailResponse>(
        GET_CLIENT_CARTON_DETAIL,
        {
            variables: { id },
            skip: !id || !visible,
            fetchPolicy: 'cache-and-network',
        }
    );

    const detail = data?.getClientCartonDetail;

    const translateOrderStatus = (status: string | null | undefined) =>
        status ? t(`enum.overallStatus.${status}`, { defaultValue: status }) : '-';

    const statusOptions = useMemo(() => {
        if (!detail?.parcels) return [];
        const statuses = Array.from(new Set(detail.parcels.map((p) => p.orderStatus).filter(Boolean)));
        return [
            { value: 'all', label: t('wms.clientCartons.options.all') },
            ...statuses.map((s) => ({ value: s!, label: translateOrderStatus(s) })),
        ];
    }, [detail?.parcels, t]);

    const filteredParcels = useMemo(() => {
        if (!detail?.parcels) return [];
        return detail.parcels.filter((p) => {
            const matchSearch = !searchText || p.trackingNumber.toLowerCase().includes(searchText.toLowerCase());
            const matchStatus = statusFilter === 'all' || p.orderStatus === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [detail?.parcels, searchText, statusFilter]);

    const parcelColumns: TableColumnsType<ClientCartonParcel> = [
        {
            title: t('wms.clientCartons.columns.parcel'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            render: (text: string, record: ClientCartonParcel) => {
                if (!record.orderId) {
                    return text;
                }
                return (
                    <Button
                        type="link"
                        style={{ padding: 0, height: 'auto' }}
                        onClick={() => {
                            setSelectedOrderId(record.orderId!.toString());
                            setParcelDetailVisible(true);
                        }}
                    >
                        {text}
                    </Button>
                );
            },
        },
        {
            title: t('wms.clientCartons.columns.parcelStatus'),
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            render: (status: string | null) => translateOrderStatus(status),
        },
    ];

    const scanStatusText = detail?.scanStatus === 1
        ? t('wms.clientCartons.status.scanned')
        : t('wms.clientCartons.status.notScanned');

    const renderContent = () => {
        if (!visible) return null;

        if (loading && !detail) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <Spin size="large" />
                </div>
            );
        }

        if (error || !detail) {
            return (
                <Alert
                    message={t('wms.clientCartons.detail.noData')}
                    description={error?.message}
                    type="error"
                    showIcon
                    action={
                        <Space>
                            <Button size="small" onClick={() => refetch()}>{t('components.button.retry')}</Button>
                            <Button size="small" onClick={onClose}>{t('components.button.close')}</Button>
                        </Space>
                    }
                />
            );
        }

        return (
            <div style={{ padding: '5px 24px' }}>
                {/* 大包基本信息 */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.columns.masterWaybillNo')}</Text>
                            <Text>{detail.masterWaybillNo || '-'}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.filters.createdTime')}</Text>
                            <Text>{detail.createdAt ? formatISODateTime(detail.createdAt) : '-'}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.columns.destinationPort')}</Text>
                            <Text>{detail.arrivePortCode || '-'}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.detail.cartonStatus')}</Text>
                            <Tag color={detail.scanStatus === 1 ? 'green' : 'default'}>{scanStatusText}</Tag>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.filters.scanPort')}</Text>
                            <Text>{detail.scanPort || '-'}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.columns.parcelCount')}</Text>
                            <Text strong>{detail.parcelCount}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.detail.inboundedParcelCount')}</Text>
                            <Text>{detail.onlineParcelCount}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text type="secondary" className="min-w-24">{t('wms.clientCartons.detail.individualScanCount')}</Text>
                            <Text>{detail.individualScanCount}</Text>
                        </div>
                    </div>
                </div>

                {/* 筛选区 */}
                <div className="flex items-center gap-3" style={{ marginBottom: '12px' }}>
                    <Input.Search
                        placeholder={t('wms.clientCartons.detail.searchParcel') || ''}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 240 }}
                        allowClear
                    />
                    <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={statusOptions}
                        style={{ width: 200 }}
                    />
                </div>

                {/* 包裹列表 */}
                <Table
                    rowKey="trackingNumber"
                    columns={parcelColumns}
                    dataSource={filteredParcels}
                    pagination={false}
                    size="middle"
                    style={{ marginBottom: '8px' }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '8px' }}>
                    <Button onClick={onClose} className="w-auto min-w-24">
                        <span className="font-bold">{t('components.button.ok')}</span>
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Drawer
            title={`${t('wms.clientCartons.detail.title')} - ${clientCartonNo || ''}`}
            placement="right"
            onClose={onClose}
            open={visible}
            width={800}
            destroyOnClose
        >
            {renderContent()}

            {parcelDetailVisible && (
                <CentralParcelDetailDrawer
                    showInterceptionExemptionAlert={true}
                    visible={parcelDetailVisible}
                    onClose={() => setParcelDetailVisible(false)}
                    orderId={selectedOrderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
            )}
        </Drawer>
    );
};

export default ClientCartonDetailDrawer;
